import type { AnchorShape, AnchorShapeKind } from '@discovr/types';

import { decimate } from './geometry';

export type Tool = AnchorShapeKind;

/** The markup palette — must match the BE's FrameColours. */
export const FRAME_COLOURS = ['#DC2626', '#F59E0B', '#10B981', '#06B6D4', '#2563EB', '#FFFFFF'] as const;
export type FrameColour = (typeof FRAME_COLOURS)[number];

/** A box/arrow smaller than this on both axes is a click, not a mark. */
export const MIN_BOX_FRACTION = 0.015;
export const MIN_PEN_POINTS = 2;
/**
 * Stored cap (BE `limits.MaxPenPoints`). The bake uses the raw path; storage
 * is decimated. Low because the send is JSON under the WAF's 8 KB body check.
 */
export const MAX_PEN_POINTS = 64;
/**
 * Decimals a stored coordinate keeps: 1/10 000 of the frame, a fifth of a
 * pixel on a 1920px side — full float precision would only cost bytes.
 */
export const STORED_COORD_DECIMALS = 4;

/** A committed shape keeps the un-decimated pen path for baking only. */
export type DraftShape = AnchorShape & { rawPts?: [number, number][] };

export interface AnnotationState {
  tool: Tool;
  colour: FrameColour;
  shapes: DraftShape[];
  draft?: DraftShape;
}

export type AnnotationAction =
  | { type: 'tool'; tool: Tool }
  | { type: 'colour'; colour: FrameColour }
  | { type: 'start'; x: number; y: number }
  | { type: 'move'; x: number; y: number }
  | { type: 'end' }
  | { type: 'undo' }
  | { type: 'clear' };

export function initialAnnotation(): AnnotationState {
  return { tool: 'box', colour: FRAME_COLOURS[0], shapes: [] };
}

function clamp01(v: number): number {
  return Math.min(1, Math.max(0, v));
}

function startShape(tool: Tool, colour: FrameColour, x: number, y: number): DraftShape {
  const cx = clamp01(x);
  const cy = clamp01(y);
  if (tool === 'pen') {
    return { k: 'pen', c: colour, rawPts: [[cx, cy]] };
  }
  return { k: tool, c: colour, x1: cx, y1: cy, x2: cx, y2: cy };
}

function extendShape(draft: DraftShape, x: number, y: number): DraftShape {
  const cx = clamp01(x);
  const cy = clamp01(y);
  if (draft.k === 'pen') {
    return { ...draft, rawPts: [...(draft.rawPts ?? []), [cx, cy]] };
  }
  return { ...draft, x2: cx, y2: cy };
}

/**
 * Decide whether a finished draft is a real mark. Boxes/arrows need a
 * minimum extent on at least one axis; pens need two points. Pen strokes
 * are decimated for storage (`pts`) while `rawPts` stays for the bake.
 */
export function commitDraft(draft: DraftShape | undefined): DraftShape | undefined {
  if (!draft) return undefined;
  if (draft.k === 'pen') {
    const raw = draft.rawPts ?? [];
    if (raw.length < MIN_PEN_POINTS) return undefined;
    return { ...draft, pts: decimate(raw, MAX_PEN_POINTS), rawPts: raw };
  }
  const w = Math.abs((draft.x2 ?? 0) - (draft.x1 ?? 0));
  const h = Math.abs((draft.y2 ?? 0) - (draft.y1 ?? 0));
  if (w < MIN_BOX_FRACTION && h < MIN_BOX_FRACTION) return undefined;
  return draft;
}

/** Strip bake-only data before the shapes are stored on the note. */
export function toStoredShapes(shapes: DraftShape[]): AnchorShape[] {
  return shapes.map(({ rawPts: _rawPts, ...stored }) => roundShape(stored));
}

function roundCoord(v: number): number {
  return Number(v.toFixed(STORED_COORD_DECIMALS));
}

/** The shape with every coordinate at `STORED_COORD_DECIMALS`; absent fields stay absent. */
function roundShape({ x1, y1, x2, y2, pts, ...rest }: AnchorShape): AnchorShape {
  const rounded: AnchorShape = { ...rest };
  if (x1 !== undefined) rounded.x1 = roundCoord(x1);
  if (y1 !== undefined) rounded.y1 = roundCoord(y1);
  if (x2 !== undefined) rounded.x2 = roundCoord(x2);
  if (y2 !== undefined) rounded.y2 = roundCoord(y2);
  if (pts) rounded.pts = pts.map(([x, y]) => [roundCoord(x), roundCoord(y)]);
  return rounded;
}

export function annotationReducer(state: AnnotationState, action: AnnotationAction): AnnotationState {
  switch (action.type) {
    case 'tool':
      return { ...state, tool: action.tool };
    case 'colour':
      return { ...state, colour: action.colour };
    case 'start':
      return { ...state, draft: startShape(state.tool, state.colour, action.x, action.y) };
    case 'move':
      if (!state.draft) return state;
      return { ...state, draft: extendShape(state.draft, action.x, action.y) };
    case 'end': {
      const committed = commitDraft(state.draft);
      return { ...state, draft: undefined, shapes: committed ? [...state.shapes, committed] : state.shapes };
    }
    case 'undo':
      return { ...state, shapes: state.shapes.slice(0, -1) };
    case 'clear':
      return { ...state, shapes: [], draft: undefined };
  }
}
